/**
 * simplifyForUser — фильтр текста ответа агента для Simple Mode (чат как ChatGPT).
 *
 * Что прячем (это нужно только разработчику в Developer Mode):
 *  - Секцию «## Краткий обзор» / «## Выдержки из прочитанных файлов»
 *    (best-effort fallback из jarvis-core/app/api/chat_v3.py:4103, попадает в
 *    done.text когда LLM не сгенерил нормальный финальный ответ).
 *  - Backend-секцию «## План\n1. … 2. …» (digest шагов после tool-calls).
 *  - Любой fenced code-блок вокруг абсолютных путей `/root/...` или `/aicores-ide/...`
 *    (это сырой dump прочитанных файлов).
 *  - Заголовки `### `/root/...`` / `### `/aicores-ide/...`` целиком с привязанным code-блоком.
 *  - Хвостовую техническую метку `_Запрос: …_`.
 *  - Inline-упоминания абсолютных путей (`/root/aicores-ide/jarvis-core/app/main.py`)
 *    заменяются на короткое имя файла (`main.py`).
 *  - Shell-команды `cat /root/...`, `cd /aicores-ide/...` и подобные — удаляются.
 *
 * Что НЕ трогаем:
 *  - Markdown-таблицы (структурированный итог — это ровно то, что ChatGPT-стиль).
 *  - Списки и обычный текст ответа.
 *  - Code-блоки без абсолютных серверных путей (это пользовательский код, ему место в ответе).
 *
 * Принцип: "если пользователь simple-режима увидит этот фрагмент — он не поймёт его смысла,
 * но техническая суть для разработчика останется в Developer Mode благодаря тому, что в
 * store сохраняется оригинальный текст. Фильтр применяется только при рендере simple-mode".
 */
export function simplifyForUser(text: string): string {
  if (!text) return text;
  let s = String(text);

  // 1. Секция «## Краткий обзор» / «## Выдержки из прочитанных файлов» — от заголовка
  //    до следующего заголовка верхнего уровня (## или #) или до конца текста.
  s = s.replace(
    /^#{2,3}\s*(?:Краткий обзор|Выдержки из прочитанных файлов)[^\n]*\n[\s\S]*?(?=^#{1,3}\s|^_Запрос:|\n*$(?![\s\S]))/gim,
    "",
  );

  // 2. Заголовок-путь `### `/root/...`` или `### `/aicores-ide/...`` со следующим за ним fenced-code.
  s = s.replace(
    /^#{2,4}\s*`\/(?:root|aicores-ide)\/[^`]+`\s*\n```[a-z]*\n[\s\S]*?\n```\s*\n?/gm,
    "",
  );

  // 3. Любой fenced-блок, в котором встречаются абсолютные пути /root/... или /aicores-ide/...
  //    (сырой dump или shell-вывод). Не трогаем code-блоки без таких путей.
  s = s.replace(
    /```[a-z0-9_-]*\n([\s\S]*?\/(?:root|aicores-ide)\/[\s\S]*?)\n```\s*\n?/g,
    "",
  );

  // 4. Backend-digest «## План» с пронумерованными итогами.
  s = s.replace(
    /^#{2,3}\s*План\s*\n(?:[ \t]*[0-9]+\.[^\n]*\n?)+/gim,
    "",
  );

  // 5. Хвостовая метка `_Запрос: …_`.
  s = s.replace(/^_Запрос:[^\n]*_\s*$/gim, "");

  // 6. Inline-упоминания абсолютных путей → basename.
  //    /root/aicores-ide/jarvis-core/app/main.py → main.py
  //    /aicores-ide/web-ui/src/App.tsx → App.tsx
  s = s.replace(
    /\/(?:root\/aicores-ide|aicores-ide|root)\/[A-Za-z0-9_./-]+/g,
    (match) => {
      const parts = match.split("/").filter(Boolean);
      return parts[parts.length - 1] || match;
    },
  );

  // 7. Строки-команды над этими путями: «cat /root/...», «cd /aicores-ide/...» и т.п.
  //    После шага 6 пути уже заменены на basename, но если кусок не попал под regex,
  //    подчищаем явные shell-вызовы абсолютных путей.
  s = s.replace(
    /^[ \t]*\$?\s*(cat|cd|ls|grep|head|tail|less|more|find|sed|awk)\s+\/[A-Za-z0-9_./-]+[^\n]*\n?/gm,
    "",
  );

  // 8. Удаляем строки-«code копировать» — backend иногда вставляет такие как UI-метки.
  s = s.replace(/^[ \t]*code[ \t]+(?:копировать|copy)[ \t]*\n?/gim, "");

  // 8.1. Прячем backend-инструкции про «подтверждаю xxxxxxxx» — теперь это
  //      делает кнопка «Подтвердить» (ConfirmCard). Пользователю simple-режима
  //      не нужно видеть «копировать `подтверждаю ed97df51`».
  //
  //      Покрываем варианты:
  //        - «Требуется подтверждение: `подтверждаю ed97df51`»
  //        - «⚠️ ТРЕБУЕТСЯ ПОДТВЕРЖДЕНИЕ [ed97df51]: '...'»
  //        - «Подтвердить: `подтверждаю` или `подтверждаю ed97df51` (либо POST /api/v1/confirm/ed97df51).»
  //        - изолированные строки `подтверждаю ed97df51` (если backend оставил такие).
  s = s.replace(
    /^[ \t]*[⚠️!\s]*ТРЕБУЕТСЯ\s+ПОДТВЕРЖДЕНИЕ\s*\[[0-9a-fA-F]{6,12}\][^\n]*\n?/gim,
    "",
  );
  s = s.replace(
    /^[ \t]*Требуется\s+подтверждение[^\n]*`?подтверждаю[^`\n]*`?[^\n]*\n?/gim,
    "",
  );
  s = s.replace(
    /^[ \t]*Подтвердить:[^\n]*`?подтверждаю[^\n]*\n?/gim,
    "",
  );
  s = s.replace(
    /^[ \t]*\(?либо\s+POST\s+\/api\/v1\/confirm\/[0-9a-fA-F]{6,12}\)?\.?\s*\n?/gim,
    "",
  );
  s = s.replace(
    /^[ \t]*`?\s*подтверждаю(?:\s+[0-9a-fA-F]{6,12})?\s*`?\s*\n?/gim,
    "",
  );
  // Inline-вхождения «подтверждаю xxxxxxxx» в произвольном тексте (на случай,
  // если они остались внутри предложения) — заменяем на «(см. кнопку ниже)».
  s = s.replace(
    /`?\s*подтверждаю\s+[0-9a-fA-F]{6,12}\s*`?/g,
    "",
  );

  // 9. Чистим лишние пустые строки, тримим края.
  s = s.replace(/\n{3,}/g, "\n\n").trim();

  return s;
}

/**
 * isContentEmptyForSimple — true если после фильтрации в simple mode текст пуст или
 * содержит только тривиальный fallback («Готово.»).
 * Используется чтобы решить, показывать ли вообще bubble (если backend прислал только
 * технический dump — пользователю simple-режима показывать нечего).
 */
export function isContentEmptyForSimple(text: string): boolean {
  const cleaned = simplifyForUser(text || "").trim();
  if (!cleaned) return true;
  if (cleaned.length < 6) return true;
  if (/^(готово|done)\.?$/i.test(cleaned)) return true;
  return false;
}
